import {useState} from 'react'
import {
  Modal,
  Button,
  Stack,
  styled,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions
} from '@mui/material'
import {Location} from 'iconsax-reactjs'
import LocationPicker from 'web-common/components/location/LocationPicker'
import {LocationBoxModal} from '../actions/MapLocationPicker'
import {useTranslation} from 'react-i18next'

const IconWrapper = styled('div')(({theme}) => ({
  width: 'auto',
  height: 'auto',
  marginInline: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))

interface LocationValue {
  lat: number
  lon: number
  address: string
  regions?: string[]
  range?: number
}

interface LocationPickerButtonProps {
  onLocationPicked?: (
    location: {lat: number; lon: number},
    address: string,
    regions?: string[],
    range?: number,
    variant?: string
  ) => void
}

const LocationPickerButton: React.FC<LocationPickerButtonProps> = ({
  onLocationPicked
}) => {
  const [t] = useTranslation()
  const [choiceOpen, setChoiceOpen] = useState<boolean>(false)
  const [pickerOpen, setPickerOpen] = useState<boolean>(false)
  const [variant, setVariant] = useState<'location' | 'range'>('location')
  const [value, setValue] = useState<LocationValue | undefined>()

  const handleChoiceOpen = () => {
    setChoiceOpen(true)
  }

  const handleChoiceClose = () => {
    setChoiceOpen(false)
  }

  const handleVariantSelect = (selected: 'location' | 'range') => {
    setVariant(selected)
    setChoiceOpen(false)
    setPickerOpen(true)
  }

  const handleClosePicker = () => {
    setPickerOpen(false)
    setValue(undefined)
  }

  const handleLocationChange = (
    location: {lat: number; lon: number},
    address: string,
    regions?: string[],
    range?: number
  ) => {
    setValue({lat: location.lat, lon: location.lon, address, regions, range})
  }

  const handleConfirm = () => {
    if (value && onLocationPicked) {
      onLocationPicked(
        {lat: value.lat, lon: value.lon},
        value.address,
        value.regions,
        value.range,
        variant
      )
    }
    handleClosePicker()
  }

  return (
    <>
      <IconWrapper>
        <IconButton
          size="small"
          color="primary"
          onClick={handleChoiceOpen}
          sx={(theme) => ({
            width: 40,
            height: 40,
            padding: 0,
            color: theme.palette.common.black
          })}
        >
          <Location size={24} />
        </IconButton>
      </IconWrapper>

      <Dialog
        open={choiceOpen}
        onClose={handleChoiceClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('fnd-assistant-dialog-location-title')}</DialogTitle>
        <DialogActions sx={{justifyContent: 'center'}}>
          <Button
            variant="contained"
            onClick={() => handleVariantSelect('location')}
          >
            {t('fnd-assistant-dialog-location-type-1')}
          </Button>
          <Button
            variant="contained"
            onClick={() => handleVariantSelect('range')}
          >
            {t('fnd-assistant-dialog-location-type-2')}
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={pickerOpen} onClose={handleClosePicker}>
        <LocationBoxModal>
          <Box flex={1} overflow="hidden">
            <LocationPicker
              country="il"
              variant={variant}
              value={value}
              onChange={handleLocationChange}
            />
          </Box>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            paddingTop={2}
          >
            <Button
              variant="contained"
              color="inherit"
              size="large"
              onClick={handleClosePicker}
            >
              {t('fnd-assistant-common-cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={!value}
            >
              {t('fnd-assistant-common-confirm')}
            </Button>
          </Stack>
        </LocationBoxModal>
      </Modal>
    </>
  )
}

export default LocationPickerButton
